import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import TableFreezePopover from '../TableFreeze/TableFreezePopover';
import useTableFreeze from '../TableFreeze/useTableFreeze';

import {
  getTableConfiguration,
  parseTablePinConfigFromApiResponse,
} from './services/table.configuration.service';

export { buildWidgetFreezeTableKey } from '../../../utils/widget-freeze-table-key.helper';

const PFFreezeDataTable = ({
  children,
  showFreeze = false,
  freezeScrollHeight = '400px',
  tableKey,
  freezePositionDeps = [],
  freezeFillSlot = false,
  ...props
}) => {
  const positionDepsArray = Array.isArray(freezePositionDeps)
    ? freezePositionDeps
    : [];
  const { header, ...restTableProps } = props;
  const dataTableRef = useRef(null);
  let resolvedHeader = null;
  if (header != null) {
    if (typeof header === 'function') {
      try {
        resolvedHeader = header({ props: restTableProps });
      } catch {
        resolvedHeader = header();
      }
    } else {
      resolvedHeader = header;
    }
  }

  const [loadedFixHeader, setLoadedFixHeader] = useState(false);
  const [loadedFrozenLeft, setLoadedFrozenLeft] = useState([]);

  useEffect(() => {
    if (!showFreeze || !tableKey) return undefined;
    let cancelled = false;
    getTableConfiguration(tableKey).then(res => {
      if (cancelled) return;
      const parsed = parseTablePinConfigFromApiResponse(res);
      setLoadedFixHeader(parsed.fixHeader);
      setLoadedFrozenLeft(parsed.frozenLeft);
    });
    return () => {
      cancelled = true;
    };
  }, [showFreeze, tableKey]);

  const columnElements = React.Children.toArray(children).filter(child => {
    if (!React.isValidElement(child)) return false;

    const isPrimeColumn =
      child.type === Column || child.type?.displayName === 'Column';

    const hasColumnLikeProps =
      Object.prototype.hasOwnProperty.call(child.props || {}, 'field') ||
      Object.prototype.hasOwnProperty.call(child.props || {}, 'header');

    return isPrimeColumn || hasColumnLikeProps;
  });

  const getHeaderText = header => {
    if (!header) return '';

    if (typeof header === 'string') {
      return header.trim();
    }

    if (React.isValidElement(header)) {
      const { children } = header.props || {};

      if (Array.isArray(children)) {
        return children
          .filter(child => typeof child === 'string')
          .join('')
          .trim();
      }

      if (typeof children === 'string') {
        return children.trim();
      }
    }

    return '';
  };

  const columnKey = (col, index) => {
    const fieldVal =
      typeof col.props.field === 'string' ? (col.props.field || '').trim() : '';
    const headerVal =
      typeof col.props.header === 'string'
        ? (col.props.header || '').trim()
        : '';
    if (fieldVal) return col.props.field;
    if (headerVal) return col.props.header;
    return `col-${index}`;
  };

  const freezePopoverLabel = (col, _index) => {
    if (!React.isValidElement(col)) return 'Empty field';
    const p = col.props || {};
    const pin = p.freezePinLabel;
    if (typeof pin === 'string' && pin.trim() !== '') return pin;
    const fl = p.freezeLabel;
    if (typeof fl === 'string' && fl.trim() !== '') return fl;
    const billingHeaderLabel =
      tableKey === 'billing-payment' ? getHeaderText(p.header) : '';
    if (billingHeaderLabel) return billingHeaderLabel;
    if (typeof p.header === 'string') {
      const headerText = p.header.trim();
      if (headerText.toLowerCase() === 'none') return '';
      if (headerText !== '') return headerText;
    }
    const f = p.field;
    if (f != null && String(f).trim() !== '') {
      const label = String(f).trim().replace(/_/g, ' ').replace(/\s+/g, ' ');
      return label
        ? label
            .split(' ')
            .map(word =>
              word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : word
            )
            .join(' ')
        : label;
    }
    return 'Actions';
  };

  const orderedFields = columnElements.map((col, index) =>
    columnKey(col, index)
  );

  const freezeColumnsSignature = columnElements
    .map((col, index) => {
      if (React.isValidElement(col) && col.props?.selectionMode) {
        return '';
      }
      const header = freezePopoverLabel(col, index);
      return `${index}:${columnKey(col, index)}:${header}`;
    })
    .join('\0');

  const freezeAvailableColumns = useMemo(() => {
    return columnElements
      .map((col, index) => ({ col, index }))
      .filter(({ col }) => {
        return !(
          React.isValidElement(col) &&
          col.props &&
          col.props.selectionMode
        );
      })
      .map(({ col, index }) => ({
        field: columnKey(col, index),
        header: freezePopoverLabel(col, index),
      }));
  }, [freezeColumnsSignature]);

  const {
    freezeButtonRef,
    freezePopoverRef,
    showFreezePopover,
    togglePopover,
    freezeHeader,
    freezeHeaderDraft,
    setFreezeHeaderDraft,
    freezeOptions,
    setDraftColumnChecked,
    confirmFreeze,
    frozenCount,
    appliedFrozenColumns,
    tableClassName,
    getColumnClassName,
  } = useTableFreeze({
    tableRef: dataTableRef,
    orderedFields,
    availableColumns: freezeAvailableColumns,
    positionDeps: [restTableProps.value?.length, ...positionDepsArray],
    initialFreezeHeader: loadedFixHeader,
    initialFrozenColumns: loadedFrozenLeft,
  });

  const handleConfigLoaded = useCallback((fixHeader, frozenLeft) => {
    setLoadedFixHeader(Boolean(fixHeader));
    setLoadedFrozenLeft(Array.isArray(frozenLeft) ? frozenLeft : []);
  }, []);

  const getSavePayload = (freezeHeader, frozenLeft) => ({
    table_key: tableKey,
    column_configuration: JSON.stringify({
      frozenLeft: frozenLeft ?? [],
      fixHeader: freezeHeader,
    }),
  });

  const needsScrollable = Boolean(showFreeze);
  const effectiveScrollHeight = needsScrollable
    ? props.scrollHeight || freezeScrollHeight
    : undefined;

  const fillSlotScrollHeight = freezeFillSlot
    ? props.scrollHeight || 'flex'
    : undefined;

  useEffect(() => {
    if (!showFreeze || !freezeHeader) return;

    const syncFirstHeaderRowHeight = () => {
      const root = dataTableRef.current?.getElement?.() || dataTableRef.current;
      const wrapper = root?.querySelector?.('.p-datatable-wrapper');
      const thead = root?.querySelector?.('.p-datatable-thead');
      const firstRow = root?.querySelector?.(
        '.p-datatable-thead tr:first-child'
      );
      if (!wrapper || !firstRow) return;
      const h1 = firstRow.getBoundingClientRect().height;
      if (h1 > 0) {
        wrapper.style.setProperty(
          '--pf-freeze-thead-row1-height',
          `${Math.round(h1)}px`
        );
      }
      if (thead) {
        const th = thead.getBoundingClientRect().height;
        if (th > 0) {
          wrapper.style.setProperty(
            '--pf-freeze-thead-sticky-stack-height',
            `${Math.round(th)}px`
          );
        }
      }
    };

    syncFirstHeaderRowHeight();
    const raf = requestAnimationFrame(syncFirstHeaderRowHeight);
    const tmo = window.setTimeout(syncFirstHeaderRowHeight, 150);

    const rootForObserver =
      dataTableRef.current?.getElement?.() || dataTableRef.current;
    const row = rootForObserver?.querySelector?.(
      '.p-datatable-thead tr:first-child'
    );
    const ro =
      row && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(syncFirstHeaderRowHeight)
        : null;
    if (ro && row) ro.observe(row);

    window.addEventListener('resize', syncFirstHeaderRowHeight);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(tmo);
      ro?.disconnect();
      window.removeEventListener('resize', syncFirstHeaderRowHeight);
      const cleanupRoot =
        dataTableRef.current?.getElement?.() || dataTableRef.current;
      const w = cleanupRoot?.querySelector?.('.p-datatable-wrapper')?.style;
      w?.removeProperty?.('--pf-freeze-thead-row1-height');
      w?.removeProperty?.('--pf-freeze-thead-sticky-stack-height');
    };
  }, [
    showFreeze,
    freezeHeader,
    needsScrollable,
    effectiveScrollHeight,
    restTableProps.value?.length,
    appliedFrozenColumns,
  ]);

  const freezeContent =
    showFreeze && columnElements.length > 0 ? (
      <TableFreezePopover
        freezeButtonRef={freezeButtonRef}
        freezePopoverRef={freezePopoverRef}
        showFreezePopover={showFreezePopover}
        togglePopover={togglePopover}
        freezeHeaderDraft={freezeHeaderDraft}
        setFreezeHeaderDraft={setFreezeHeaderDraft}
        freezeOptions={freezeOptions}
        setDraftColumnChecked={setDraftColumnChecked}
        confirmFreeze={confirmFreeze}
        frozenCount={frozenCount}
        tableKey={tableKey}
        onConfigLoaded={handleConfigLoaded}
        getSavePayload={getSavePayload}
      />
    ) : null;

  let columnIndex = 0;
  const enhancedChildren = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return child;

    const isPrimeColumn =
      child.type === Column || child.type?.displayName === 'Column';
    const hasColumnLikeProps =
      Object.prototype.hasOwnProperty.call(child.props || {}, 'field') ||
      Object.prototype.hasOwnProperty.call(child.props || {}, 'header');

    if (!isPrimeColumn && !hasColumnLikeProps) return child;

    const column = child;
    const col = columnElements[columnIndex];
    const key = columnKey(col, columnIndex);
    columnIndex += 1;
    const existingClassName = column.props.className || '';
    const existingHeaderClassName = column.props.headerClassName || '';
    const freezeClassName = getColumnClassName(key, existingClassName);
    const freezeHeaderClassName = getColumnClassName(
      key,
      existingHeaderClassName
    );

    const normalizedHeader =
      typeof column.props.header === 'string' &&
      column.props.header.trim().toLowerCase() === 'none'
        ? ''
        : column.props.header;

    return React.cloneElement(column, {
      className: freezeClassName,
      headerClassName: freezeHeaderClassName,
      ...(normalizedHeader !== column.props.header
        ? { header: normalizedHeader }
        : {}),
      ...(showFreeze ? { frozen: false } : {}),
    });
  });

  const combinedHeader = showFreeze ? (
    <div
      className={`pf-table-freeze-combined-header flex ${restTableProps?.from === 'map' ? 'align-items-start' : 'align-items-center'} w-full gap-2 mb-1`}
    >
      <div className="pf-table-freeze-combined-header-content flex-1 w-full">
        {resolvedHeader}
      </div>
      <div className="pf-table-freeze-combined-header-action flex align-items-center flex-shrink-0">
        {freezeContent}
      </div>
    </div>
  ) : (
    resolvedHeader
  );

  const mergedTableClassName = [
    tableClassName,
    restTableProps.className,
    'pf-data-table',
  ]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  const tableProps = {
    ...restTableProps,
    header: combinedHeader,
    ref: dataTableRef,
    className: mergedTableClassName,
  };

  if (freezeFillSlot) {
    tableProps.scrollable = true;
    tableProps.scrollHeight = fillSlotScrollHeight;
  } else if (needsScrollable) {
    tableProps.scrollable = true;
    tableProps.scrollHeight = effectiveScrollHeight;
  }

  const rootClassNames = [
    showFreeze
      ? 'pf-freeze-datatable-root position-relative flex flex-column gap-2'
      : '',
    freezeFillSlot && showFreeze ? 'pf-freeze-datatable-root--fill-slot' : '',
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    <div className={rootClassNames || undefined}>
      <DataTable {...tableProps}>{enhancedChildren}</DataTable>
    </div>
  );
};

export default PFFreezeDataTable;
